import { EventEmitter, Injectable, Injector, OnDestroy } from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { shouldShowBulkEditProgress } from "@app/components/bulk-edit-progress-overlay/bulk-edit-loading.util";
import { BulkOperationProgressDialogComponent } from "@app/components/bulk-operation-progress-dialog/bulk-operation-progress-dialog.component";
import { SONNER_DEFAULT_CONFIG, SONNER_ERROR_TOAST_DURATION_MS } from "@app/config/sonner";
import { CreateUndoService } from "@app/services/create-undo/create-undo.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import { dismissTimeEntryUndoToast, TIME_ENTRY_UNDO_TOAST_ID } from "@app/services/time-entry-undo-toast.util";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { Logger, MyTimesQuery, MyTimesService, Time } from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import { merge } from "lodash-es";
import { ExternalToast, toast } from "ngx-sonner";
import type { EditUndoEvent, EditUndoOffer, SingleEditUndoOffer } from "./edit-undo.types";

export type {
	EditUndoEvent,
	EditUndoOffer,
	SingleEditUndoOffer,
	TimeBulkEditDialogCloseResult,
} from "./edit-undo.types";

const log = new Logger("EditUndoService");

/** Time window to undo edits */
const UNDO_TIMEOUT_MS = 12000;
const UPDATE_CHUNK_SIZE = 50;

@UntilDestroy()
@Injectable({
	providedIn: "root",
})
export class EditUndoService implements OnDestroy {
	private pending = new Map<string, { toastId: string; originals: Time[] }>();
	private reverting = new Set<string>();

	/** Emitted after entries were reverted to their pre-edit state via undo. */
	readonly reverted = new EventEmitter<EditUndoEvent>(true);

	private get store(): MyTimesStore {
		return this.myTimesQuery.__store__ as MyTimesStore;
	}

	constructor(
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private translateService: TranslateService,
		private dialog: MatDialog,
		private injector: Injector,
		private mixedTimesSyncService: MixedTimesSyncService
	) {}

	/** Clears tracked undo state and dismisses the shared undo toast. */
	dismissPendingUndoToasts(): void {
		dismissTimeEntryUndoToast();
		this.pending.clear();
	}

	/** Dismisses all time-entry undo toasts (create, edit, and delete). */
	dismissAllPendingUndoToasts(): void {
		this.dismissPendingUndoToasts();
		this.injector.get(CreateUndoService).dismissPendingUndoToasts();
		this.injector.get(DeletionUndoService).dismissPendingUndoToasts();
	}

	/**
	 * Shows a success toast with undo for a single successfully updated entry.
	 * Call after the edit API work completes.
	 */
	offerSingleUndo(offer: SingleEditUndoOffer): void {
		this.offerUndo({
			originals: [offer.original],
			updatedResults: [offer.updated],
			successDescriptionKey: offer.successDescriptionKey ?? "success.updated",
		});
	}

	/**
	 * Shows a success/warn toast with undo for successfully updated entries.
	 * Call after the edit API work completes.
	 */
	offerUndo(offer: EditUndoOffer): void {
		const originalsToRestore = this.resolveOriginalsToRestore(offer);
		if (!originalsToRestore.length) return;

		this.dismissAllPendingUndoToasts();

		const key = `editUndo-${Date.now()}`;
		const undoAction = {
			label: this.translateService.instant("utils.undo"),
			onClick: (): void => {
				if (!this.pending.has(key)) return;
				void this.revert(key, originalsToRestore);
			},
		};
		const toastOptions = {
			duration: UNDO_TIMEOUT_MS,
			action: undoAction,
			onDismiss: () => this.pending.delete(key),
			onAutoClose: () => this.pending.delete(key),
		};

		if (offer.partial?.failed) {
			toast.error(
				null,
				merge({}, { ...SONNER_DEFAULT_CONFIG }, {
					duration: SONNER_ERROR_TOAST_DURATION_MS,
					description: this.translateService.instant("time.edit-partial-failed", {
						count: offer.partial.failed,
					}),
				} as ExternalToast)
			);
		}

		const successDescription = offer.successDescriptionKey
			? this.translateService.instant(offer.successDescriptionKey)
			: offer.partial
				? this.translateService.instant("time.edit-partial-success", {
						count: offer.partial.updated,
					})
				: this.translateService.instant("success.updated-plural");

		toast.success(
			null,
			merge({}, { ...SONNER_DEFAULT_CONFIG }, {
				id: TIME_ENTRY_UNDO_TOAST_ID,
				description: successDescription,
				...toastOptions,
			} as ExternalToast)
		);

		this.pending.set(key, { toastId: TIME_ENTRY_UNDO_TOAST_ID, originals: originalsToRestore });
	}

	private notifyRevertResult(restoredCount: number, failedCount: number): void {
		if (restoredCount > 0) {
			toast.success(
				null,
				merge({}, { ...SONNER_DEFAULT_CONFIG }, {
					duration: 4000,
					description: this.translateService.instant(
						restoredCount === 1 ? "success.restored" : "success.restored-plural"
					),
				} as ExternalToast)
			);
		}
		if (failedCount > 0) {
			toast.error(
				null,
				merge({}, { ...SONNER_DEFAULT_CONFIG }, {
					duration: SONNER_ERROR_TOAST_DURATION_MS,
					description: this.translateService.instant(
						failedCount === 1 ? "utils.restore_failed" : "utils.restore_failed_plural",
						failedCount === 1 ? {} : { count: failedCount }
					),
				} as ExternalToast)
			);
		}
	}

	private resolveOriginalsToRestore({ originals, updatedResults }: EditUndoOffer): Time[] {
		const updatedIds = new Set(updatedResults.map((t) => t.id));
		const byId = new Map(originals.map((t) => [t.id, t]));
		return [...updatedIds].map((id) => byId.get(id)).filter((t): t is Time => !!t);
	}

	private async revert(key: string, originals: Time[]): Promise<void> {
		if (this.reverting.has(key)) return;

		const pending = this.pending.get(key);
		if (pending) {
			dismissTimeEntryUndoToast();
			this.pending.delete(key);
		}

		this.reverting.add(key);
		const loadingRef = shouldShowBulkEditProgress(originals.length)
			? this.openRevertLoadingDialog(originals.length)
			: undefined;
		loadingRef?.componentInstance.setProgress(0);
		try {
			const befores = new Map(
				originals.map((o) => [o.id, structuredClone(this.myTimesQuery.getEntity(o.id) ?? o)])
			);
			const restored = await this.updateInChunks(originals, loadingRef);
			this.notifyRevertResult(restored.length, originals.length - restored.length);
			if (restored.length) {
				this.store.upsertMany(restored);
				this.mixedTimesSyncService.closeCorrectionDialogIfResolved(restored);
				for (const reverted of restored) {
					const before = befores.get(reverted.id);
					if (before) {
						this.mixedTimesSyncService.handleProjectTimeDialogClose(reverted, before);
					}
				}
				this.reverted.emit({ key });
			}
		} catch (err) {
			log.error("Edit undo failed", err);
			this.notifyRevertResult(0, originals.length);
		} finally {
			loadingRef?.close();
			this.reverting.delete(key);
		}
	}

	private openRevertLoadingDialog(total: number): MatDialogRef<BulkOperationProgressDialogComponent> {
		return this.dialog.open(BulkOperationProgressDialogComponent, {
			data: { total, mode: "undo" },
			disableClose: true,
			closeOnNavigation: false,
		});
	}

	private refreshRevertProgress(
		loadingRef: MatDialogRef<BulkOperationProgressDialogComponent> | undefined,
		processed: number,
		failed = 0
	) {
		loadingRef?.componentInstance?.setProgress(processed, failed);
	}

	private async updateInChunks(
		entries: Time[],
		loadingRef?: MatDialogRef<BulkOperationProgressDialogComponent>
	): Promise<Time[]> {
		const allResults: Time[] = [];
		let processed = 0;
		let failed = 0;
		this.refreshRevertProgress(loadingRef, processed, failed);

		for (const chunk of this.chunkArray(entries, UPDATE_CHUNK_SIZE)) {
			try {
				const res = await this.myTimesService.update(...chunk).toPromise();
				if (!res?.length) throw new Error("Edit undo chunk returned no results");
				allResults.push(...res);
				failed += chunk.length - res.length;
				processed += chunk.length;
				this.refreshRevertProgress(loadingRef, processed, failed);
			} catch (err) {
				log.warn("Edit undo chunk failed, retrying individually", err);
				for (const entry of chunk) {
					try {
						const res = await this.myTimesService.update(entry).toPromise();
						const updated = res?.[0];
						if (updated) allResults.push(updated);
						else failed++;
					} catch (entryErr) {
						failed++;
						log.error(entryErr);
					}
					processed++;
					this.refreshRevertProgress(loadingRef, processed, failed);
				}
			}
		}
		return allResults;
	}

	private chunkArray<T>(arr: T[], size: number): T[][] {
		const chunks: T[][] = [];
		for (let i = 0; i < arr.length; i += size) {
			chunks.push(arr.slice(i, i + size));
		}
		return chunks;
	}

	ngOnDestroy(): void {
		this.dismissPendingUndoToasts();
	}
}
